import { Config } from 'config/envConfig';
import React, { useEffect, useState } from 'react';
import {
    faFileExport,
    faPlus,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import {
    alert,
    AlertType,
    Button,
    Input,
    LoadingView,
    TextArea,
    Toggle,
} from '@quyva/shared-ui';
import { getUrl } from '../utils/getUrl';
import { ProcessType } from '@quyva/common';
import { TaskProgressType } from '@quyva/common';
import { Table } from 'antd';
import { message } from 'antd';

function ExtractSupportInfo({
    extractInfoItem,
    socket,
    setExtractInfoMode,
    getAllReposData,
}) {
    const [itemsList, setItemsList] = useState([
        { entity: '', description: '', key: '0' },
    ]);
    const [alreadyExtractedList, setAlreadyExtractedList] = useState([]);
    const [nextKey, setNextKey] = useState(1);
    const [loadingView, setLoadingView] = useState(false);
    const [miniRepoMode, setMiniRepoMode] = useState(true);

    useEffect(() => {
        if (itemsList.length === 0) {
            setItemsList([{ entity: '', description: '', key: '0' }]);
        }
    }, [itemsList]);

    const fetchProducts = async () => {
        try {
            setLoadingView(true);
            const repoId = extractInfoItem.repoid;
            const dynamicUrl = getUrl(
                `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/`
            );
            const response = await fetch(
                `${dynamicUrl}routes_1/productsList/${repoId}/${miniRepoMode}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                console.error('Error fetching products:', errorData.detail);
                setAlreadyExtractedList([]);
                return;
            }

            const productList = await response.json();
            setAlreadyExtractedList(
                productList.map((item, index) => ({
                    ...item,
                    key: `extracted-${index}`,
                    isExtracted: true,
                }))
            );
        } catch (error) {
            alert(
                AlertType.ERROR,
                'Network error while fetching products:',
                error
            );
            setAlreadyExtractedList([]);
        } finally {
            setLoadingView(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, [miniRepoMode]);

    const handleInputChange = (key, field, value) => {
        setItemsList((prev) =>
            prev.map((item) =>
                item.key === key ? { ...item, [field]: value } : item
            )
        );
    };

    const handleAddItem = () => {
        setItemsList((prev) => [
            ...prev,
            { entity: '', description: '', key: `${nextKey}` },
        ]);
        setNextKey((prev) => prev + 1);
    };

    const handleDeleteItem = (key) => {
        setItemsList((prev) => prev.filter((item) => item.key !== key));
    };

    const checkIfEmpty = () => {
        return (
            itemsList.every((item) => item.entity.trim() === '') &&
            alreadyExtractedList.length === 0
        );
    };

    function validateNewItems(newItemsList, alreadyExtractedList) {
        // Check for empty new items list
        if (newItemsList.length === 0 && alreadyExtractedList.length === 0) {
            return 'Please add at least one entity';
        }

        // Prepare extracted entities set (case-insensitive)
        const extractedEntities = new Set(
            alreadyExtractedList
                .filter((item) => item.entity.trim() !== '')
                .map((item) => item.entity.trim().toLowerCase())
        );

        // Track duplicates and conflicts
        const duplicates = new Set();
        const conflicts = new Set();
        const seenEntities = new Set();

        // Check each new item
        for (const item of newItemsList) {
            const entity = item.entity.trim();
            if (!entity) continue; // Skip empty entries

            const normalizedEntity = entity.toLowerCase();

            // Check for duplicates within new items
            if (seenEntities.has(normalizedEntity)) {
                duplicates.add(entity);
            } else {
                seenEntities.add(normalizedEntity);
            }

            // Check for conflicts with extracted list
            if (extractedEntities.has(normalizedEntity)) {
                conflicts.add(entity);
            }
        }

        // Build error messages
        const messages = [];
        if (duplicates.size > 0) {
            const dupList = Array.from(duplicates).join(', ');
            messages.push(`remove duplicates: [${dupList}]`);
        }
        if (conflicts.size > 0) {
            const conflictList = Array.from(conflicts).join(', ');
            messages.push(`remove existing entities: [${conflictList}]`);
        }

        // Return combined error message
        return messages.length > 0
            ? `Please ${messages.join(' and ')} and try again`
            : null;
    }

    const handleConfirm = async () => {
        const cleanedList = itemsList
            .filter((item) => item.entity.trim() !== '')
            .map((item) => item);

        console.log('cleaned List :', cleanedList);
        console.log('already Extracted Data ', alreadyExtractedList);
        const finalList = [...alreadyExtractedList, ...cleanedList];

        const error = validateNewItems(cleanedList, alreadyExtractedList);

        if (error) {
            message.error(error);
            return;
        }
        const user = JSON.parse(localStorage.getItem('current'));
        const { email } = user;

        const dynamicUrl = getUrl(
            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/`
        );

        const requestBody = {
            email,
            repo: extractInfoItem,
            entity_list: finalList,
            mini_repo_mode: miniRepoMode,
        };

        const status = {
            message: `Auto extraction`,
            percentageCompletion: 0,
            process: ProcessType.SupportInfoExtraction,
            progress: TaskProgressType.STARTED,
            repoId: null,
            repoName: extractInfoItem.name,
            statusCode: 200,
            timeStamp: new Date(),
            userMail: user.email,
        };

        try {
            setExtractInfoMode(false);
            socket.emit('statusUpdate', status);

            const response = await fetch(
                `${dynamicUrl}routes_1/extract_support_info`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to submit data');
            }

            const result = await response.json();
            // console.log('API Response:', result);

            // Refresh repos list
            await getAllReposData();
        } catch (error) {
            alert(AlertType.ERROR, 'Error submitting data');
            console.error('Error submitting data:', error);
        }
    };

    const columns = [
        {
            title: 'Entity',
            dataIndex: 'entity',
            key: 'entity',
            width: '30%',
            render: (text, record) =>
                record.isExtracted ? (
                    text
                ) : (
                    <Input
                        type='text'
                        value={record.entity}
                        onChange={(e) =>
                            handleInputChange(
                                record.key,
                                'entity',
                                e.target.value
                            )
                        }
                        placeholder='Enter Entity'
                        className='w-full'
                    />
                ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            width: '60%',
            render: (text, record) =>
                record.isExtracted ? (
                    text
                ) : (
                    <TextArea
                        value={record.description}
                        onChange={(e) =>
                            handleInputChange(
                                record.key,
                                'description',
                                e.target.value
                            )
                        }
                        placeholder='Enter Description'
                        className='w-full'
                        rows={2}
                    ></TextArea>
                ),
        },
        {
            title: 'Action',
            key: 'action',
            width: '10%',
            render: (_, record) =>
                record.isExtracted ? (
                    <Button
                        icon={faTrash}
                        className='w-9 h-9'
                        hoverText='Delete Extracted Db to remove all products. Selective entity deletion is not supported'
                        type='danger'
                        disabled={true}
                    />
                ) : (
                    <Button
                        icon={faTrash}
                        className='w-9 h-9'
                        onClick={() => handleDeleteItem(record.key)}
                        hoverText='Delete entity'
                        type='danger'
                        disabled={itemsList.length <= -1}
                    />
                ),
        },
    ];

    const dataSource = [...alreadyExtractedList, ...itemsList];

    return loadingView ? (
        <div className='m-4 w-[40vw] h-[20vh] flex justify-center items-center'>
            <LoadingView loadingText={`Loading entities...`} />
        </div>
    ) : (
        <div className='m-4 max-w-3xl min-w-[40vw] '>
            <div className='flex mb-4'>
                <div className='flex  ml-auto gap-4'>
                    <div
                        className={`whitespace-nowrap ${
                            !miniRepoMode
                                ? 'text-buttons-primary'
                                : 'text-gray-400'
                        } font-semibold`}
                    >
                        Database
                    </div>
                    <Toggle
                        onChange={() => setMiniRepoMode(!miniRepoMode)}
                        checked={miniRepoMode}
                        toggleTextClassName={
                            'w-fit   font-semibold text-texts-secondary300 flex gap-4'
                        }
                    />
                    <div
                        className={`whitespace-nowrap ${
                            miniRepoMode
                                ? 'text-buttons-primary'
                                : 'text-gray-400'
                        } font-semibold`}
                    >
                        Mini Repo
                    </div>
                </div>
            </div>
            <Table
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                rowKey='key'
                scroll={{ y: 400 }}
            />

            <div className='flex w-full justify-end items-center mt-4'>
                <Button
                    icon={faPlus}
                    text='New Entity'
                    className='ml-2 mr-6'
                    onClick={handleAddItem}
                    hoverText='Add new item'
                    type='success'
                />

                <Button
                    icon={faFileExport}
                    onClick={handleConfirm}
                    text='Auto Extract Data'
                    hoverText='Start extraction process'
                    type='success'
                    disabled={checkIfEmpty()}
                >
                    Confirm
                </Button>
            </div>
        </div>
    );
}

export default ExtractSupportInfo;
